import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { createResultAdapter } from "@func-core/core/data/adapters";
import {
  createResultViewModel,
  PetParentData,
  ResultViewModel,
} from "@func-core/core/domain/view-models/result-view-model";
import { createSelectors } from "@func-core/core/utils/zustand-selectors";

type ResultState = {
  result: ResultViewModel | null;
  setResult: (result: ResultViewModel) => void;
  clearResult: () => void;
  fetchResult: (resultUuid: string) => Promise<ResultViewModel | null>;
  updatePetParentData: (resultUuid: string, data: Partial<PetParentData>) => void;
};

const useResultStoreBase = create<ResultState>()(
  devtools(
    (set, get) => {
      const adapter = createResultAdapter();

      return {
        result: null,

        setResult: (result) => set({ result }, false, "setResult"),

        clearResult: () => set({ result: null }, false, "clearResult"),

        fetchResult: async (resultUuid) => {
          const dto = await adapter.fetchResult(resultUuid);
          if (!dto) return null;

          const petParentData = adapter.getPetParentData(resultUuid);
          const viewModel = createResultViewModel(dto, petParentData);
          set({ result: viewModel }, false, "fetchResult");

          return viewModel;
        },

        updatePetParentData: (resultUuid, data) => {
          adapter.savePetParentData(resultUuid, data);

          const currentResult = get().result;
          if (currentResult && currentResult.uuid === resultUuid) {
            const updatedPetParent = {
              ...currentResult.petParent,
              ...data,
              countryCode: data.countryCode ?? currentResult.petParent.countryCode,
            };
            const updatedResult = {
              ...currentResult,
              petParent: updatedPetParent,
            };
            set({ result: updatedResult }, false, "updatePetParentData");
          }
        },
      };
    },
    {
      name: "Result Store",
      enabled: process.env.NODE_ENV === "development",
    }
  )
);

export const useResultStore = createSelectors(useResultStoreBase);
